import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {
  ApiId,
  ATTACHMENT_MODEL,
  CHECKLIST_LINE_MODEL,
  DIVE_LOG_LINE_MODEL,
  DIVE_OPERATION_MODEL,
  IAppRecord,
  RISK_ASSESSMENT_MODEL,
  TOOLBOX_TALK_MODEL,
} from "@app/models";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { AuthService } from "../services/auth.service";

@Injectable({
  providedIn: "root",
})
export class OdooService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private readonly versionedModels = new Set([
    ATTACHMENT_MODEL,
    CHECKLIST_LINE_MODEL,
    DIVE_LOG_LINE_MODEL,
    DIVE_OPERATION_MODEL,
    RISK_ASSESSMENT_MODEL,
    TOOLBOX_TALK_MODEL,
  ]);

  constructor() {}

  private getContext(context = {}) {
    return JSON.stringify({
      allowed_company_ids: [this.authService.getCompany()],
      ...context,
      dopa_app: 1,
    });
  }

  private withServerVersion<T extends Record<string, any>>(record: T): T {
    if (record?.["dopa_revision"]) {
      record = {
        ...record,
        server_version: record["dopa_revision"],
      };
      delete record["dopa_revision"];
    }
    return record;
  }

  public create(
    model: string,
    values: any,
    context = {}
  ): Observable<IAppRecord> {
    if ("id" in values) {
      delete values.id;
    }
    return this.http.post<any>(`${environment.apiUrl}/${model}`, values, {
      params: { context: this.getContext(context) },
    });
  }

  public read(model: string, id: ApiId, context = {}): Observable<IAppRecord> {
    const params: Record<string, string> = {
      context: this.getContext({ ...context, active_test: 0 }),
    };
    if (this.versionedModels.has(model)) {
      params["include_fields"] = JSON.stringify(["dopa_revision"]);
    }
    return this.http
      .get<any>(`${environment.apiUrl}/${model}/${id}`, {
        params,
      })
      .pipe(map((record) => this.withServerVersion(record)));
  }

  public search(
    model: string,
    options: {
      context?: Record<string, any>;
      filters?: any[];
    } = {}
  ): Observable<IAppRecord[]> {
    const params: Record<string, string> = {
      context: this.getContext(options.context),
    };
    if (options.filters) {
      params["filters"] = JSON.stringify(options.filters);
    }
    if (this.versionedModels.has(model)) {
      params["include_fields"] = JSON.stringify(["dopa_revision"]);
    }
    return this.http
      .get<any>(`${environment.apiUrl}/${model}`, {
        params,
      })
      .pipe(
        map((odooData) =>
          odooData.results.length > 0
            ? odooData.results.map((record) => this.withServerVersion(record))
            : []
        )
      );
  }

  public update(
    model: string,
    id: ApiId,
    values: any,
    context = {}
  ): Observable<any> {
    return this.http.put<any>(`${environment.apiUrl}/${model}/${id}`, values, {
      params: { context: this.getContext(context) },
    });
  }

  public conditionalUpdate(
    model: string,
    id: ApiId,
    values: any,
    expectedVersion: string,
    context = {}
  ): Observable<{ version: string }> {
    return this.http.put<{ version: string }>(
      `${environment.apiUrl}/dopa/${model}/${id}`,
      {
        expected_version: expectedVersion,
        values,
      },
      { params: { context: this.getContext(context) } }
    );
  }

  public delete(model: string, id: ApiId, context = {}) {
    return this.http.delete<any>(`${environment.apiUrl}/${model}/${id}`, {
      params: { context: this.getContext(context) },
    });
  }

  public call(model: string, method: string, context = {}) {
    return this.http.get<any>(`${environment.apiUrl}/${model}/0/${method}`, {
      params: { context: this.getContext(context) },
    });
  }

  public file(id: any, context = {}) {
    return this.http.get<any>(`${environment.apiUrl}/ir.attachment/${id}`, {
      params: {
        include_fields: ["datas", "name", "id"],
        exclude_fields: ["*"],
        context: this.getContext(context),
      },
    });
  }
}
